import {
  Button,
  Flex,
  Grid,
  Heading,
  Popover,
  Select,
  IconButton,
  Box,
  TextField,
} from "@radix-ui/themes";
import { ListFilter } from "lucide-react";
import { Column, RowData } from "@tanstack/react-table";

declare module "@tanstack/react-table" {
  //allows us to define custom properties for our columns
  interface ColumnMeta<TData extends RowData, TValue> {
    filterVariant?: "text" | "dateRange" | "select" | "number";
    selectOptions?: { value: any; title: string }[];
  }
}

export default function ColumnFilter({
  column,
}: {
  column: Column<any, unknown>;
}) {
  const columnFilterValue = column.getFilterValue();
  const { filterVariant, selectOptions } = column.columnDef.meta ?? {};

  return (
    <Popover.Root>
      <Popover.Trigger>
        <IconButton size="1" variant="soft">
          <ListFilter size="16px" />
        </IconButton>
      </Popover.Trigger>
      <Popover.Content width="360px">
        {filterVariant === "dateRange" ? (
          <Flex direction="column" gap="2">
            <Grid gap="2" columns="2">
              <Box>
                <Heading as="h3" size="2" mb="1">
                  From
                </Heading>
                <TextField.Root
                  type="date"
                  value={(columnFilterValue as [string, string])?.[0] ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    column.setFilterValue((old: [string, string]) => [
                      val ? val : undefined,
                      old?.[1],
                    ]);
                  }}
                />
              </Box>
              <Box>
                <Heading as="h3" size="2" mb="1">
                  To
                </Heading>
                <TextField.Root
                  type="date"
                  value={(columnFilterValue as [number, number])?.[1] ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    column.setFilterValue((old: [number, number]) => [
                      old?.[0],
                      val ? val : undefined,
                    ]);
                  }}
                />
              </Box>
            </Grid>
            <Flex justify="end">
              <Button
                variant="soft"
                onClick={() => column.setFilterValue(undefined)}
              >
                Clear
              </Button>
            </Flex>
          </Flex>
        ) : filterVariant === "select" ? (
          <Select.Root
            value={columnFilterValue?.toString()}
            onValueChange={(value) =>
              column.setFilterValue(value === "all" ? "" : value)
            }
            defaultValue="all"
          >
            <Flex direction="column" gap="2">
              <Box className="w-full">
                <Heading as="h3" size="2" mb="2">
                  Select...
                </Heading>
                <Box className="w-full">
                  <Select.Trigger style={{ width: "inherit" }} />
                </Box>
              </Box>
              <Select.Content>
                <Select.Group>
                  {selectOptions?.map(({ value, title }) => (
                    <Select.Item key={value} value={value}>{title}</Select.Item>
                  ))}
                </Select.Group>
              </Select.Content>
            </Flex>
          </Select.Root>
        ) : filterVariant === "number" ? (
        <Flex direction="column" gap="2">
            <Grid gap="2" columns="2">
              <Box>
                <Heading as="h3" size="2" mb="1">
                  From
                </Heading>
                <TextField.Root
                  type="number"
                  value={(columnFilterValue as [string, string])?.[0] ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    column.setFilterValue((old: [string, string]) => [
                      val ? val : undefined,
                      old?.[1],
                    ]);
                  }}
                />
              </Box>
              <Box>
                <Heading as="h3" size="2" mb="1">
                  To
                </Heading>
                <TextField.Root
                  type="number"
                  value={(columnFilterValue as [number, number])?.[1] ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    column.setFilterValue((old: [number, number]) => [
                      old?.[0],
                      val ? val : undefined,
                    ]);
                  }}
                />
              </Box>
            </Grid>
            <Flex justify="end">
              <Button
                variant="soft"
                onClick={() => column.setFilterValue(undefined)}
              >
                Clear
              </Button>
            </Flex>
          </Flex>
        ) : (
          <Box>
            <Flex direction="column" gap="2">
              <Heading as="h3" size="2" mb="1">
                Search...
              </Heading>
              <TextField.Root
                type="text"
                value={columnFilterValue?.toString()}
                onChange={(e) => {
                  column.setFilterValue(e.target.value);
                }}
              />
              <Flex justify="end">
                <Button
                  variant="soft"
                  onClick={() => column.setFilterValue(undefined)}
                >
                  Clear
                </Button>
              </Flex>
            </Flex>
          </Box>
        )}
      </Popover.Content>
    </Popover.Root>
  );
}
